import { useState, useRef, useCallback } from 'react';
import { Icon, Spinner } from './ui/Icon.js';
import { Button } from './ui/Button.js';
import { Select } from './Select.js';
import { apiFetch } from '../lib/api.js';

const CATEGORIES = [
  { value: 'discovery', label: 'Discovery' },
  { value: 'comparison', label: 'Comparison' },
  { value: 'recommendation', label: 'Recommendation' },
  { value: 'troubleshooting', label: 'Troubleshooting' },
  { value: 'review', label: 'Review' },
] as const;

// Curated locale list — gpt-4o-mini handles all of these well. Custom values
// are still accepted via the underlying input but the dropdown drives the
// common case.
const LOCALES = [
  { value: 'en', label: 'English (en)' },
  { value: 'en-US', label: 'English – US (en-US)' },
  { value: 'en-GB', label: 'English – UK (en-GB)' },
  { value: 'da', label: 'Danish (da)' },
  { value: 'sv', label: 'Swedish (sv)' },
  { value: 'no', label: 'Norwegian (no)' },
  { value: 'de', label: 'German (de)' },
  { value: 'fr', label: 'French (fr)' },
  { value: 'es', label: 'Spanish (es)' },
  { value: 'nl', label: 'Dutch (nl)' },
  { value: 'fi', label: 'Finnish (fi)' },
  { value: 'it', label: 'Italian (it)' },
  { value: 'pt', label: 'Portuguese (pt)' },
  { value: 'pt-BR', label: 'Portuguese – Brazil (pt-BR)' },
  { value: '__custom__', label: 'Custom…' },
] as const;

const PLACEHOLDERS = [
  '{{brand}}',
  '{{industry}}',
  '{{competitor_1}}',
  '{{competitor_2}}',
  '{{persona}}',
  '{{use_case}}',
  '{{location}}',
  '{{description}}',
  '{{region}}',
  '{{feature}}',
  '{{technology}}',
  '{{constraint}}',
] as const;

const PLACEHOLDER_RE = /\{\{\w+\}\}/;

export interface PromptFormData {
  template: string;
  category: string;
  locale: string;
}

export interface PromptFormProps {
  initial?: { template: string; category: string; locale: string };
  tenantId: string;
  onSubmit: (data: PromptFormData) => Promise<void>;
  onCancel: () => void;
}

const CURATED_LOCALE_VALUES: Set<string> = new Set(
  LOCALES.filter((l) => l.value !== '__custom__').map((l) => l.value as string),
);

export function PromptForm({ initial, tenantId, onSubmit, onCancel }: PromptFormProps) {
  const [template, setTemplate] = useState(initial?.template ?? '');
  const [category, setCategory] = useState(initial?.category ?? 'discovery');
  const initialLocale = initial?.locale ?? 'en';
  const initialIsCustom = !CURATED_LOCALE_VALUES.has(initialLocale);
  const [locale, setLocale] = useState(initialLocale);
  // Track whether the locale picker is in custom-text mode so the user can
  // type unusual BCP-47 values that aren't in the dropdown.
  const [localeMode, setLocaleMode] = useState<'curated' | 'custom'>(
    initialIsCustom ? 'custom' : 'curated',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Translate-specific UI state
  const [translateTarget, setTranslateTarget] = useState<string>(
    initialLocale === 'en' || initialLocale.startsWith('en') ? 'da' : initialLocale,
  );
  const [translating, setTranslating] = useState(false);
  const [translateWarning, setTranslateWarning] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasPlaceholder = PLACEHOLDER_RE.test(template);

  const handleChipClick = useCallback((placeholder: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      setTemplate((prev) => prev + placeholder);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = template.slice(0, start);
    const after = template.slice(end);
    const newValue = before + placeholder + after;
    setTemplate(newValue);
    // Restore cursor position after the inserted placeholder
    requestAnimationFrame(() => {
      textarea.focus();
      const newPos = start + placeholder.length;
      textarea.setSelectionRange(newPos, newPos);
    });
  }, [template]);

  const handleSubmit = async () => {
    if (!template.trim()) {
      setError('Template cannot be empty');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ template: template.trim(), category, locale: locale.trim() || 'en' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save prompt');
    } finally {
      setSubmitting(false);
    }
  };

  const handleTranslate = async () => {
    if (!template.trim()) {
      setError('Translate needs some template text first');
      return;
    }
    if (!translateTarget.trim()) {
      setError('Pick a target language to translate into');
      return;
    }
    setTranslating(true);
    setError(null);
    setTranslateWarning(null);
    try {
      const res = await apiFetch(`/api/v1/tenants/${tenantId}/translate/prompt`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template: template.trim(),
          target_locale: translateTarget,
          source_locale: locale && locale !== translateTarget ? locale : undefined,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; details?: string[] };
        throw new Error(body.error ?? `Translate failed (${res.status})`);
      }
      const body = (await res.json()) as {
        data: {
          translated_template: string;
          target_locale: string;
          missing_placeholders: string[];
          warning: string | null;
        };
      };
      setTemplate(body.data.translated_template);
      // Update the locale select to match the translated locale. Switch to
      // custom mode if the returned locale isn't on the curated list.
      if (CURATED_LOCALE_VALUES.has(body.data.target_locale)) {
        setLocale(body.data.target_locale);
        setLocaleMode('curated');
      } else {
        setLocale(body.data.target_locale);
        setLocaleMode('custom');
      }
      const warnings: string[] = [];
      if (body.data.missing_placeholders.length > 0) {
        warnings.push(
          `Some placeholders had to be re-appended at the end (${body.data.missing_placeholders.join(', ')}). Move them where they belong before saving.`,
        );
      }
      if (body.data.warning) warnings.push(body.data.warning);
      setTranslateWarning(warnings.length ? warnings.join(' ') : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Translate failed');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="prompt-form-modal">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl border border-[var(--hub-color-line)] bg-white">
        {/* Header — pinned so the title + close stay reachable on tall forms */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">
            {initial ? 'Edit prompt' : 'Add prompt'}
          </h2>
          <Button round onClick={onCancel} aria-label="Close">
            <Icon name="bx-x" />
          </Button>
        </div>

        {/* Scrollable body — the form (template + chips + category + locale +
            translate) is taller than short viewports, so it scrolls while the
            header and the Create/Cancel footer stay pinned. */}
        <div className="flex-1 space-y-4 overflow-y-auto px-6 py-4">
          {/* Template textarea */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Template
            </label>
            <textarea
              ref={textareaRef}
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows={5}
              className="mt-1 block w-full rounded-lg border border-[var(--hub-color-line)] px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
              placeholder="e.g. What are the best {{industry}} brands like {{brand}}?"
              data-testid="prompt-template-input"
            />
            {/* Placeholder chips */}
            <div className="mt-2 flex flex-wrap gap-1.5">
              {PLACEHOLDERS.map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => handleChipClick(p)}
                  className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-xs font-medium text-violet-700 transition-colors hover:bg-violet-100"
                  data-testid={`chip-${p.replace(/[{}]/g, '')}`}
                >
                  {p}
                </button>
              ))}
            </div>
            {!hasPlaceholder && template.length > 0 && (
              <p className="mt-1 text-xs text-amber-600">
                No placeholder detected. Recommended for brand-aware prompts
                (e.g. <code className="font-mono">{'{{brand}}'}</code>) — but
                you can still create the prompt as a generic question to test
                whether the brand surfaces unprompted.
              </p>
            )}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Category
            </label>
            <div className="mt-1">
              <Select
                options={CATEGORIES.map((c) => ({ value: c.value, label: c.label }))}
                value={category}
                onChange={setCategory}
                placeholder="Select category"
                className="w-full"
                data-testid="prompt-category-select"
              />
            </div>
          </div>

          {/* Locale */}
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Locale
            </label>
            <div className="mt-1 flex items-center gap-2">
              {localeMode === 'curated' ? (
                <Select
                  options={LOCALES.map((l) => ({ value: l.value, label: l.label }))}
                  value={CURATED_LOCALE_VALUES.has(locale) ? locale : '__custom__'}
                  onChange={(v) => {
                    if (v === '__custom__') {
                      setLocaleMode('custom');
                    } else {
                      setLocale(v);
                    }
                  }}
                  placeholder="Select locale"
                  className="w-56"
                  data-testid="prompt-locale-select"
                />
              ) : (
                <>
                  <input
                    type="text"
                    value={locale}
                    onChange={(e) => setLocale(e.target.value)}
                    className="block w-40 rounded-lg border border-[var(--hub-color-line)] px-3 py-1.5 text-sm text-slate-900 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500"
                    placeholder="e.g. ja-JP"
                    data-testid="prompt-locale-input"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setLocaleMode('curated');
                      if (!CURATED_LOCALE_VALUES.has(locale)) setLocale('en');
                    }}
                    className="text-xs text-violet-600 hover:underline"
                  >
                    Pick from list
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Translate */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center gap-2">
              <Icon name="bx-globe-alt" className="text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Translate template</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Replaces the template above with a {LOCALES.find((l) => l.value === translateTarget)?.label ?? translateTarget} version. <code className="font-mono">{'{{placeholders}}'}</code> are preserved.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Select
                options={LOCALES.filter((l) => l.value !== '__custom__').map((l) => ({ value: l.value, label: l.label }))}
                value={translateTarget}
                onChange={setTranslateTarget}
                placeholder="Target language"
                className="w-56"
                data-testid="prompt-translate-target"
              />
              <Button variant="ghost" onClick={handleTranslate} disabled={translating || !template.trim()} data-testid="prompt-translate-button">
                {translating ? <Spinner /> : <Icon name="bx-globe-alt" />}
                {translating ? 'Translating…' : 'Translate'}
              </Button>
            </div>
            {translateWarning && (
              <p className="mt-2 text-xs text-amber-700" data-testid="prompt-translate-warning">
                {translateWarning}
              </p>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600" data-testid="prompt-form-error">{error}</p>
          )}
        </div>

        {/* Actions — pinned footer so Create/Cancel are always reachable
            regardless of how tall the form grows. */}
        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting || !template.trim()} data-testid="prompt-form-submit">
            {submitting ? 'Saving...' : initial ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}
